// /@handle: an approved creator's public profile (their name, handle, bio and links; courses come later).
import MediaAttachment from '../platform/MediaAttachment';
import Markdown from '../components/Markdown';
import { useSettings } from '../store/settings';
import React, { useEffect, useState } from 'react';
import { Linking, Text, View } from 'react-native';
import { useRoute, type RouteProp } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { Body, Button, Caption, EmptyState, Screen } from '../components/ui';
import * as social from '../platform/social';
import { useHeader } from '../hooks';
import { font, radius, spacing, usePalette } from '../theme';
import { parseShare } from '../logic/share';
import type { AllParams } from '../navigation';
import type { CreatorPost, CreatorProfile } from '../types';

export default function CreatorScreen() {
  const { t } = useTranslation();
  const p = usePalette();
  const follows = useSettings(s => s.followedCreators);
  const setSettings = useSettings(s => s.set);
  const handle = useRoute<RouteProp<AllParams, 'Creator'>>().params?.handle ?? '';
  const [creator, setCreator] = useState<CreatorProfile | null | undefined>(undefined);
  const [posts, setPosts] = useState<CreatorPost[]>([]);
  useEffect(() => {
    social.creatorProfile(handle).then(setCreator, () => setCreator(null));
    social.creatorPosts(handle).then(r => setPosts(r.posts), () => setPosts([]));
  }, [handle]);
  useHeader({ title: creator ? `@${creator.handle}` : '' }, [creator?.handle]);
  if (creator === null) return <Screen><EmptyState icon="user" title={t('creators.notFound')} /></Screen>;
  if (!creator) return <Screen />;
  return (
    <Screen>
      <View style={{ alignItems: 'center', gap: spacing.s }}>
        <Text accessibilityRole="header" style={{ fontSize: font.title, fontWeight: '800', color: p.text, textAlign: 'center' }}>{creator.displayName}</Text>
        <Caption>@{creator.handle} · {t('creators.badge')}</Caption>
      </View>
      <Button label={t(follows.includes(handle) ? 'creators.unfollow' : 'creators.follow')} onPress={() => setSettings({ followedCreators: follows.includes(handle) ? follows.filter(value => value !== handle) : [...follows, handle] })} />
      <Body>{creator.bio}</Body>
      {creator.links.map(url => <Button key={url} label={new URL(url).hostname.replace(/^www\./, '')} icon="link" variant="secondary" onPress={() => Linking.openURL(url)} />)}
      {posts.map(post => {
        const share = parseShare(post.recipe);
        return (
          <View key={post.id} style={{ backgroundColor: p.card, borderRadius: radius.l, padding: spacing.l, gap: spacing.s }}>
            <Caption>{new Date(post.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}{post.edited ? ` · ${t('social.edited')}` : ''}</Caption>
            {post.caption ? <Markdown>{post.caption}</Markdown> : null}
            {post.photo ? <MediaAttachment media={{ uri: social.creatorPhotoSource(post.id).uri, mimeType: post.mediaType ?? undefined }} seed={post.id} style={{ width: '100%', aspectRatio: 4 / 3, borderRadius: radius.m }} /> : null}
            {share ? <Text style={{ fontWeight: '600', color: p.text }}>{t('social.sharedRecipe', { title: share.recipe.title })}</Text> : null}
          </View>
        );
      })}
      <Caption>{t('creators.coursesSoon')}</Caption>
    </Screen>
  );
}
